import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useAHUControl } from '@/contexts/ahu-control-context';
import { useEnumMappings } from '@/contexts/enum-mappings-context';
import { SignalHigh, SignalLow, SignalMedium, Power, Pause } from 'lucide-react';
import { FanSpeed } from '@/types';

interface FanControlProps {
  onChange: (fanSpeed: string) => void;
  ahuFan: string | null;
}

// Helper function to standardize fan speed values
const standardizeFanSpeed = (speed: string): string => {
  if (!speed) return '';

  // Convert to lowercase for case-insensitive comparison
  const lowerSpeed = speed.toLowerCase();

  // Map various forms to standardized values
  if (lowerSpeed === 'medium') return 'med';
  if (lowerSpeed === 'med') return 'med';
  if (lowerSpeed === 'ultra low') return 'ultra-low';
  if (lowerSpeed === 'ultra-low') return 'ultra-low';
  if (lowerSpeed === 'fan stop') return 'stop';
  if (lowerSpeed === 'stop') return 'stop';
  if (lowerSpeed === 'auto') return 'auto';
  if (lowerSpeed === 'low') return 'low';
  if (lowerSpeed === 'high') return 'high';

  // Log unexpected values
  console.log(`Standardizing unexpected fan speed value: ${speed}`);

  // For any other value, just return the lowercase version
  return lowerSpeed;
};

export function FanControl({ ahuFan, onChange }: FanControlProps) {
  const { enumMappings } = useEnumMappings();

  // Standardize ahuFan to handle capitalized enum values and medium/med inconsistency
  const [fanSpeed, setFanSpeed] = useState(standardizeFanSpeed(ahuFan || ''));

  // Get available fan speeds from enumMappings or fall back to hardcoded defaults
  const availableFanSpeeds = useMemo(() => {
    // If we have enum mappings from the backend, use them
    if (Object.values(enumMappings.fanSpeed).length > 0) {
      console.log('Fan speed enum mappings:', enumMappings.fanSpeed);

      // Get unique fan speed values from the enum mappings
      const speeds = Object.values(enumMappings.fanSpeed)
        .map(value => typeof value === 'string' ? value : '')
        .filter((value, index, self) =>
          // Filter out invalid, unfix, and duplicates
          value &&
          value !== 'invalid' &&
          value !== 'unfix' &&
          self.indexOf(value) === index
        ) as FanSpeed[];

      console.log('Filtered fan speeds from enum mappings:', speeds);

      // If we have valid speeds from the backend, use them
      if (speeds.length > 0) {
        // Ensure we have at least the basic fan speeds
        const essentialSpeeds = ['auto', 'low', 'med', 'high'];
        const missingSpeeds = essentialSpeeds.filter(speed => {
          // Check if the speed or its standardized form is missing
          const standardized = standardizeFanSpeed(speed);
          return !speeds.some(s => standardizeFanSpeed(s) === standardized);
        });

        if (missingSpeeds.length > 0) {
          console.log('Adding missing essential fan speeds:', missingSpeeds);
          return [...speeds, ...missingSpeeds] as FanSpeed[];
        }

        return speeds;
      }
    }

    // Fall back to defaults if no valid speeds from backend
    console.log('Using default fan speeds');
    return ['auto', 'low', 'med', 'high'] as FanSpeed[];
  }, [enumMappings.fanSpeed]);

  const getIconForSpeed = (speed: string) => {
    // Standardize speed value for consistent comparison
    const standardSpeed = standardizeFanSpeed(speed);
    switch (standardSpeed) {
      case 'auto': return "A";
      case 'low': return <SignalLow className={cn("w-[16px] h-[16px] text-primary", fanSpeed === standardSpeed && "text-white")} />;
      case 'med': return <SignalMedium className={cn("w-[16px] h-[16px] text-primary", fanSpeed === standardSpeed && "text-white")} />;
      case 'high': return <SignalHigh className={cn("w-[16px] h-[16px] text-primary", fanSpeed === standardSpeed && "text-white")} />;
      case 'ultra-low': return <SignalLow className={cn("w-[12px] h-[12px] text-primary", fanSpeed === standardSpeed && "text-white")} />;
      case 'stop': return <Pause className={cn("w-[16px] h-[16px] text-primary", fanSpeed === standardSpeed && "text-white")} />;
      default: return "A";
    }
  };

  return (
    <div className="px-2 pt-[6px] pb-[10px] bg-bg rounded-lg border border-light-grey flex flex-col gap-1">
      <div className="text-dark-blue heading-7">Fan Speed</div>
      <div className="flex gap-1.5 flex-wrap">
        {availableFanSpeeds.map((speed) => (
          <button
            key={speed}
            onClick={() => {
              // Store standardized version in state but send original case to onChange
              setFanSpeed(standardizeFanSpeed(speed))
              onChange(speed)
            }}
            className={cn(
              "flex flex-col items-center gap-0.5",
              "w-7 text-[11px]",
              // Standardized comparison to handle capitalized enum values and medium/med inconsistency
              fanSpeed === standardizeFanSpeed(speed) ? "text-primary" : "text-dark-grey"
            )}
          >
            <div className={cn(
              "w-7 h-7 flex items-center justify-center rounded-md",
              // Standardized comparison to handle capitalized enum values and medium/med inconsistency
              fanSpeed === standardizeFanSpeed(speed)
                ? "bg-primary/10 shadow-popup bg-alto"
                : "bg-white"
            )}>
              <div className={cn(
                "w-4 h-4 heading-7 text-center",
                // Standardized comparison to handle capitalized enum values and medium/med inconsistency
                fanSpeed === standardizeFanSpeed(speed) ? "text-white" : "text-primary"
              )}>
                {getIconForSpeed(speed)}
              </div>
            </div>
            <span className="paragraph-11 capitalize">
              {/* Display friendly names for special cases */}
              {standardizeFanSpeed(speed) === 'med' ? 'Med' :
                standardizeFanSpeed(speed) === 'ultra-low' ? 'Ultra' :
                  standardizeFanSpeed(speed) === 'stop' ? 'Stop' :
                    speed}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}